// Shared pieces for the vendor readiness checklist, used by BOTH the
// Qualifications tab and the Overview summary card. Extracted so the two
// surfaces cannot drift on stage titles, status colours, or how a recorded
// stage is attributed.
//
// The 5 stage codes MUST match the server catalog
// (VendorQualificationStageCatalog). Stages inform onboarding but gate
// NOTHING — no vendor status transition checks them, and none should: the
// entity carries a written decision against a "must pass all 5 before
// Active" gate.

import { Clock } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import { formatDateInTz } from "@/design-system/lib/format";
import {
  vendorQualificationStatusLabel,
  type VendorQualificationStageDto,
  type VendorQualificationStageStatus,
} from "@/api/portfolioAdmin/vendorQualificationsApi";

export const VENDOR_QUALIFICATION_STAGES = [
  {
    code: "background-check",
    title: "Background check",
    note: "Owner identity + criminal history. 3-year cadence.",
  },
  {
    code: "insurance-verification",
    title: "Insurance verification",
    note: "General liability + W/C as applicable. 1-year cadence.",
  },
  {
    code: "reference-checks",
    title: "Reference checks",
    note: "Two client references + one banking reference.",
  },
  {
    code: "capability-assessment",
    title: "Capability assessment",
    note: "Service-quality interview + sample work review.",
  },
  {
    code: "w9-collection",
    title: "W-9 collection",
    note: "Current-tax-year W-9 on file.",
  },
] as const;

/** A stage the server has no row for. The read path synthesizes these too;
 *  this is the client-side twin for a partial/failed load. */
export const pendingStage = (code: string): VendorQualificationStageDto => ({
  stageCode: code,
  status: "pending",
  statusDate: null,
  note: null,
  evidenceUrl: null,
  modifiedAt: null,
  recordedByUserId: null,
  recordedByName: null,
  statusSource: "staff",
  statusSourceEvent: null,
});

const STATUS_CHIP_CLASSES: Record<VendorQualificationStageStatus, string> = {
  pending: "bg-white-warm text-gray-mid",
  "in-progress": "bg-status-info/20 text-status-info",
  complete: "bg-status-success/20 text-status-success",
  waived: "bg-white-warm text-gray-mid",
  "not-applicable": "bg-white-warm text-gray-mid",
};

export function StageStatusChip({
  status,
}: {
  status: VendorQualificationStageStatus;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-space-1 px-space-2 py-[2px] rounded-sm text-[10px] font-display font-bold uppercase tracking-[0.16em]",
        STATUS_CHIP_CLASSES[status] ?? "bg-white-warm text-gray-mid",
      )}
      data-testid={`vendor-qual-status-${status}`}
    >
      {status === "pending" ? <Clock size={12} aria-hidden="true" /> : null}
      {vendorQualificationStatusLabel(status)}
    </span>
  );
}

/**
 * "Recorded by Casey Analyst, 12 Mar 2026" for a staff-set stage, or
 * "Verified automatically when the W-9 was accepted, 12 Mar 2026" for one an
 * event set. Null when nobody and nothing has recorded this stage yet.
 *
 * Step 5 W5 — until W4 landed the W-9 acceptance hook, every row here was a
 * staff member's claim and naming the claimant was the whole story. Now two
 * kinds of writer exist, and the difference matters: a stage a PERSON vouched
 * for and a stage an EVENT observed carry different weight, and a reader who
 * cannot tell them apart is worse off than one who assumed everything was a
 * claim. An unresolvable user still falls back to "a staff member" — never a
 * raw id.
 *
 * `modifiedAt` is an INSTANT (BaseEntity, stamped from the clock), so it should
 * render in the VIEWER's zone. This is a pure function and cannot read a hook,
 * so the caller passes `tz` — the `useDateFormatter().tz` → pure-helper handoff
 * this codebase already uses for table-column factories.
 *
 * Both production callers supply it — `VendorReadinessCard` and
 * `tabs/VendorQualificationsTab`. That is worth stating, because a parameter
 * whose callers all omit it is a destination with no door: it reads as a
 * completed fix and ships nothing. This one was in that state for the length
 * of one workstream and is not any more.
 *
 * Omitting `tz` still falls back to `FALLBACK_TIMEZONE` (America/Phoenix), so
 * a new caller that forgets fails quietly. `src/test/viewerZoneSupply.test.ts`
 * is what catches that: it discovers zoneless calls by walking the tree, so it
 * fails for a file that does not exist yet.
 */
export function stageRecordedLabel(
  stage: VendorQualificationStageDto,
  tz?: string | null,
): string | null {
  const when = stage.modifiedAt ? formatDateInTz(stage.modifiedAt, tz) : null;

  if (stage.statusSource === "system") {
    const what = SYSTEM_EVENT_LABELS[stage.statusSourceEvent ?? ""] ?? "a system event";
    return when ? `Verified automatically by ${what}, ${when}` : `Verified automatically by ${what}`;
  }

  if (!stage.recordedByUserId && !stage.modifiedAt) return null;
  const who = stage.recordedByName ?? "a staff member";
  return when ? `Recorded by ${who}, ${when}` : `Recorded by ${who}`;
}

/** Event token → what a reader should be told happened. Keep in step with
 *  VendorQualificationStageEvents on the server. */
const SYSTEM_EVENT_LABELS: Record<string, string> = {
  "vendor-w9-accepted": "an accepted W-9",
};
